import React, { useEffect, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';

import ActionButton from '../../../components/ActionButton/ActionButton';
import CustomCheckbox from '../../../components/Inputs/Checkbox/Checkbox';
import CustomAutoExpandingTextarea from '../../../components/Inputs/AutoExpandingTextarea/AutoExpandingTextarea';
import DefaultCustomSelect from '../../../components/Inputs/DefaultCustomSelect/DefaultCustomSelect';
import DefaultCustomInput from '../../../components/Inputs/DefaultCustomInput/DefaultCustomInput';
import { CheckboxContainer } from '../../../components/Inputs/Checkbox/styles';
import {
  cropConnectApiClient,
  CropConnectRoleDefinitions,
  DeliveryPlanSettingsDto,
  DeliveryPlanSettingsDtoCropConnectApiResponse,
} from '../../../../services/cropConnectApiClient';
import { ButtonContainer, SubSectionTitle } from './styles';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';

const DeliveryPlanSettings = () => {
  const { t } = useI18n();

  const [deliveryPlanSettings, setDeliveryPlanSettings] =
    useState<DeliveryPlanSettingsDto | null>(null);
  const [growerGroup, setGrowerGroup] = useState('');
  const [availableGroups, setAvailableGroups] = useState<
    CropConnectRoleDefinitions[]
  >([]);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState('');
  const [hasBeenEdited, setHasBeenEdited] = useState(false);
  const handleGrowerGroupChange = (event: SelectChangeEvent<unknown>) => {
    setGrowerGroup(event.target.value as string);
  };

  useEffect(() => {
    cropConnectApiClient.getDeliveryPlanSettingsApplicableRoles().then((r) => {
      setAvailableGroups(r.data ?? []);
    });
  }, []);

  useEffect(() => {
    if (growerGroup) {
      const growerGroupDefinition =
        CropConnectRoleDefinitions[
          growerGroup as keyof typeof CropConnectRoleDefinitions
        ];
      cropConnectApiClient
        .getDeliveryPlanSettings(growerGroupDefinition)
        .then((r) => {
          setDeliveryPlanSettings(r.data ?? null);
        })
        .catch((e: DeliveryPlanSettingsDtoCropConnectApiResponse) => {
          if (e.statusCode === 'NotFound') {
            setDeliveryPlanSettings({
              applicableTo: growerGroupDefinition,
              daysBeforeExactDeliveryDateVisible: 21,
              deliveryPlanVisible: true,
            });
          } else {
            console.error(e);
          }
        });
    }
  }, [growerGroup]);

  useEffect(() => {
    if (saveSuccessMessage) {
      setSaveSuccessMessage('');
    }
  }, [deliveryPlanSettings]);

  const growerGroupOptions = availableGroups.map((g) => {
    return { value: g, label: g };
  });

  return (
    <>
      <SubSectionTitle>
        {t(TranslationKeys.administration_delivery_plan_settings_title)}
      </SubSectionTitle>
      <DefaultCustomSelect
        label={t(TranslationKeys.administration_grower_group_label) as string}
        value={growerGroup}
        onChange={handleGrowerGroupChange}
        options={growerGroupOptions}
        placeholder=""
      />

      {growerGroup && (
        <>
          <CheckboxContainer>
            <CustomCheckbox
              checked={deliveryPlanSettings?.deliveryPlanVisible ?? false}
              onChange={(e) => {
                setDeliveryPlanSettings({
                  ...deliveryPlanSettings,
                  deliveryPlanVisible: e.target.checked,
                });
                setHasBeenEdited(true);
              }}
              label={
                t(
                  TranslationKeys.administration_show_delivery_plan_label
                ) as string
              }
            />
          </CheckboxContainer>

          <CustomAutoExpandingTextarea
            value={deliveryPlanSettings?.notVisibleMessage ?? ''}
            onChange={(e) => {
              setDeliveryPlanSettings({
                ...deliveryPlanSettings,
                notVisibleMessage: e.target.value,
              });
              setHasBeenEdited(true);
            }}
            placeholder={
              t(
                TranslationKeys.administration_not_visible_message_placeholder
              ) as string
            }
            label={
              t(
                TranslationKeys.administration_not_visible_message_label
              ) as string
            }
          />

          <DefaultCustomInput
            label={t(TranslationKeys.administration_days_ahead_label) as string}
            type="number"
            value={
              deliveryPlanSettings?.daysBeforeExactDeliveryDateVisible?.toString() ??
              '21'
            }
            onChange={(e) => {
              setDeliveryPlanSettings({
                ...deliveryPlanSettings,
                daysBeforeExactDeliveryDateVisible: Number.parseInt(
                  e.target.value
                ),
              });
              setHasBeenEdited(true);
            }}
          />

          <ButtonContainer>
            <ActionButton
              variant="ghost"
              buttonText={t(TranslationKeys.cancel_button)}
              onClickAction={setGrowerGroup.bind(this, '')}
            />
            <ActionButton
              variant="accept-blue"
              buttonText={t(TranslationKeys.save_button)}
              onClickAction={() => {
                if (deliveryPlanSettings) {
                  cropConnectApiClient
                    .updateDeliveryPlanSettings({
                      applicableTo:
                        CropConnectRoleDefinitions[
                          growerGroup as keyof typeof CropConnectRoleDefinitions
                        ],
                      daysBeforeExactDeliveryDateVisible:
                        deliveryPlanSettings?.daysBeforeExactDeliveryDateVisible,
                      deliveryPlanVisible:
                        deliveryPlanSettings?.deliveryPlanVisible,
                      notVisibleMessage:
                        deliveryPlanSettings?.notVisibleMessage,
                    })
                    .then((r) => {
                      if (r.success) {
                        setSaveSuccessMessage(
                          t(
                            TranslationKeys.delivery_plan_saved_message
                          ) as string
                        );
                        setHasBeenEdited(false);
                      }
                    });
                }
              }}
              disabled={!hasBeenEdited}
            />
          </ButtonContainer>

          {saveSuccessMessage && (
            <div style={{ color: 'green', marginTop: '10px' }}>
              {saveSuccessMessage}
            </div>
          )}
        </>
      )}
      <SubSectionTitle>
        {t(TranslationKeys.administration_translations)}
      </SubSectionTitle>
      <div style={{ marginBottom: '20px' }}>
        <span>{TranslationKeys.delivery_plan_no_additional_info}</span>:{' '}
        {t(TranslationKeys.delivery_plan_no_additional_info)}
      </div>
    </>
  );
};

export default DeliveryPlanSettings;
